// === E65 TV — Curated Official Videos Library ===
// Order within each category is significant. Categories match the user spec.
// Only YouTube IDs from the official Eiffel 65 YouTube channel + verified
// official label uploads (Warner / Bliss Corporation) are used here.
// Embedding restrictions are handled at runtime by the player.

export const VIDEO_CATEGORIES = [
  { id: "official",  label: "Official Music Videos", icon: "video",      color: "#00AFFF" },
  { id: "live",      label: "Live Performances",     icon: "mic",        color: "#FF3B6B" },
  { id: "tv",        label: "TV Performances",       icon: "tv",         color: "#FFD23B" },
  { id: "lyric",     label: "Lyric Videos",          icon: "music",      color: "#A78BFA" },
  { id: "remix",     label: "Remix Videos",          icon: "headphones", color: "#7CFFCB" },
  { id: "album",     label: "Album Videos",          icon: "disc",       color: "#FF7B3B" },
  { id: "bts",       label: "Behind The Scenes",     icon: "clapperboard", color: "#94A3B8" },
  { id: "rare",      label: "Rare Videos",           icon: "star",       color: "#FFB400" },
  { id: "zorotl",    label: "Zorotl Videos",         icon: "alien",      color: "#22D3EE" },
];

// Helper to keep entries DRY
const V = (id, title, year, category, album, songId, description, extra = {}) => ({
  id, title, year, category, album, songId, description,
  thumbnail: `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`,
  fallbackThumb: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
  ...extra,
});

export const E65_TV_PLAYLIST = [
  // ============== OFFICIAL MUSIC VIDEOS ==============
  V("68ugkg9RePc", "Blue (Da Ba Dee)", 1998, "official", "Europop", "blue",
    "The legendary official music video. #1 in 16 countries, certified diamond — the song that turned the world blue. Animation features Zorotl and his blue spaceship adventure."),
  V("zQ6HBbyJR34", "Move Your Body", 1999, "official", "Europop", "move-your-body",
    "Choreographed Eurodance perfection — directed by Celestino Gianotti. Top 5 across continental Europe."),
  V("DZ8PfXOV1fU", "Too Much Of Heaven", 1999, "official", "Europop", "too-much-of-heaven",
    "Ethereal angel-themed visuals matching the song's emotional chorus. Animation continues the Zorotl universe."),
  V("qqEaK6oLdNA", "One Goal", 2000, "official", "Europop", "one-goal",
    "Olympic-spirited Eurodance burner. The bridge single between Europop and Contact!"),
  V("6Hsi-xQjW8Y", "Lucky (In My Life)", 2001, "official", "Contact!", "lucky",
    "Sunlit optimism on screen. Lead single from Contact! with one of the catchiest 2000s choruses."),
  V("XXkPowMdi4Q", "Back In Time", 2001, "official", "Contact!", "back-in-time",
    "Time-travel sci-fi narrative — second Contact! single. A love letter to the past."),
  V("YM_y2qx-u5A", "80's Stars (feat. Franco Battiato)", 2002, "official", "Contact!", "80s-stars",
    "Synthwave tribute with on-brand retro visuals featuring Italian icon Franco Battiato."),
  V("T1ZTVXe3zbo", "Elephants In Amsterdam", 2002, "official", "Contact!", "elephants-amsterdam",
    "Playful surreal Eurodance — the final Contact! single, named after Amsterdam's famous painted elephants."),
  V("vaiI7j5C2wA", "Cosa Resterà (In A Song)", 2003, "official", "Eiffel 65", "cosa-restera",
    "Italian ballad version — the official Sanremo 2003 entry. A song about what stays when everything else fades."),
  V("1c5ElzGGrm8", "Viaggia Insieme A Me", 2003, "official", "Eiffel 65", "viaggia",
    "Travel with me — trance-pop journey in Italian. Official MV from the self-titled album."),
  V("E0SERnogWzQ", "Quelli Che Non Hanno Età", 2003, "official", "Eiffel 65", "quelli",
    "Italian-language anthem for those who never grow old — Sanremo 2003 performance video."),
  V("_Fh2aav8tAM", "Panico", 2016, "official", "Standalone Single", "panico",
    "The first official reunion single since 2010. The blue legacy returns — directed by Maurizio Ghiotti."),
  V("-4Vn6Y6c9U8", "Critical", 2016, "official", "Standalone Single", "critical",
    "Pulsing modern electronic pop with unmistakable Eiffel 65 DNA. Released alongside Panico in autumn 2016."),
  V("w82DdzD3SXs", "Auto Blu (feat. Shiva)", 2020, "official", "Standalone Single", "auto-blu",
    "The viral 2020s anthem with rapper Shiva. Certified Platinum (FIMI) — the blue car that took over Italian radio."),
  V("ieJdopl2Pfc", "Bestiale (feat. Loredana Bertè)", 2024, "official", "Standalone Single", "bestiale",
    "Italian dance pop with stadium-sized energy. Eiffel 65 × Loredana Bertè — released May 2024 via Warner Music Italy."),
  V("T0HGBL73FNU", "Fare A Meno Di Te (feat. Guè)", 2025, "official", "Standalone Single", "fare-a-meno",
    "Doing without you — heartfelt 2025 collaboration with rapper Guè. Released April 18, 2025 via Warner Music Italy."),

  // ============== LIVE PERFORMANCES ==============
  V("N6an6Y3UMA0", "Live at Gardaland Halloween 2025", 2025, "live", "Live Set", null,
    "Full live performance at Gardaland's Halloween 2025 event — the most recent Eiffel 65 stadium-style set."),
  V("azkd2lqgXZ4", "Full Live At Piazza Dei Sole (22.02.2020)", 2020, "live", "Live Set", null,
    "Complete 2020 live concert at Piazza Dei Sole — a 200-subscribers special by the fan archive."),
  V("LbGfW2i77ks", "Full Live At Cento (16.02.2020)", 2020, "live", "Live Set", null,
    "Full live show recorded in Cento, Italy on February 16, 2020."),
  V("3rUhI3cGC3E", "Cosa Resterà — Official Live, Turin 2011", 2011, "live", "Eiffel 65", "cosa-restera",
    "Official live recording of Cosa Resterà (In A Song) in Turin, Italy (2011)."),

  // ============== TV PERFORMANCES ==============
  V("OX6IFCjglPc", "Blue — Playlist 01/02/2025", 2025, "tv", "Europop", "blue",
    "Eiffel 65 perform Blue (Da Ba Dee) on the Italian TV show Playlist (February 1, 2025)."),
  V("lEQgeiKUvpo", "Viaggia Insieme A Me — Playlist 03/05/2025", 2025, "tv", "Eiffel 65", "viaggia",
    "Live performance of Viaggia Insieme A Me on the TV show Playlist (May 3, 2025)."),
  V("XTo6ayIGonk", "Too Much Of Heaven — Live Video Italia", 2000, "tv", "Europop", "too-much-of-heaven",
    "Iconic 2000 Italian TV performance of Too Much Of Heaven on Video Italia."),
  V("wXoUGK7jqJQ", "Back In Time — Top Of The Pops", 2001, "tv", "Contact!", "back-in-time",
    "Back In Time performed live on the legendary BBC TV show Top Of The Pops (2001)."),

  // ============== LYRIC VIDEOS ==============
  V("hdOHwPgc-lk", "The Story of 'Blue (Da Ba Dee)'", 2023, "lyric", "Europop", "blue",
    "A documentary-style retrospective on how Blue (Da Ba Dee) became a global phenomenon — features lyric breakdown."),

  // ============== RARE VIDEOS ==============
  V("Yucs6lhUR74", "Blue — Live On The Beach In France", 1999, "rare", "Europop", "blue",
    "Very rare live beach performance in France during the original Europop tour (1999/2000)."),
  V("rnL4TiLOY4Y", "Move Your Body — Live In Germany 2000", 2000, "rare", "Europop", "move-your-body",
    "Move Your Body live in Germany at the peak of the Europop era (2000)."),

  // ============== ZOROTL VIDEOS ==============
  // Zorotl features in the Blue, Move Your Body and Lucky animations — we
  // alias those cross-category for easier discovery.
  V("68ugkg9RePc", "Blue — Zorotl's first appearance", 1998, "zorotl", "Europop", "blue",
    "The blue alien Zorotl debuts in the iconic Blue (Da Ba Dee) animation. Born on Tukonr4 in the Hr453 constellation.",
    { _alias: true }),
  V("zQ6HBbyJR34", "Move Your Body — Zorotl on the dance floor", 1999, "zorotl", "Europop", "move-your-body",
    "Zorotl joins the band in a futuristic dance scene during the Move Your Body video.",
    { _alias: true }),
];

// Convenience getters
export const VIDEOS_BY_CATEGORY = (cat) => E65_TV_PLAYLIST.filter((v) => v.category === cat);
export const VIDEOS_BY_YEAR     = (year) => E65_TV_PLAYLIST.filter((v) => v.year === Number(year));
export const VIDEO_YEARS        = [...new Set(E65_TV_PLAYLIST.map((v) => v.year))].sort((a, b) => b - a);
export const VIDEO_ALBUMS       = [...new Set(E65_TV_PLAYLIST.map((v) => v.album).filter(Boolean))].sort();
